import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rciApi } from '../api/rci';
import { insightsApi } from '../api/insights';
import { featureFlagsApi } from '../api/featureFlags';

/**
 * RevealGuard — wraps a route to enforce the Day-7 archetype reveal.
 *
 * Generalized successor to the original CoherenceGuard, which wrapped
 * only `/coherence`. /coherence is not in the bottom nav, so users
 * who finished onboarding never returned to it — meaning the reveal,
 * which only this guard could fire, was unreachable from the daily
 * user flow. Every F&F user reached snapshot 7 and had their archetype
 * stamped by app/api/rci.py POST /compute, but `has_seen_reveal` stayed
 * false forever because the redirect never had a chance to run.
 *
 * Reveal fires when ALL of:
 *   1. user.has_seen_reveal === false
 *   2. snapshot.total_snapshots >= 7
 *   3. snapshot.coherence_status !== 'SAFETY_GATED'
 *
 * Once the user dismisses the reveal, RevealScreen patches
 * has_seen_reveal=true and this guard becomes a synchronous no-op for
 * the rest of their lifetime — the cheap check at the top short-circuits
 * before any network call. So the cost on the steady-state user is zero.
 *
 * Usage:
 *   <RevealGuard><Dashboard /></RevealGuard>
 *
 * Renders children if the reveal shouldn't fire, otherwise <Navigate to="/reveal">.
 */
export default function RevealGuard({ children }) {
  const { user } = useAuth();

  // Synchronous fast path: once a user has seen the reveal, the guard
  // never has to think again — no network call, no flash of blank.
  if (user?.has_seen_reveal) {
    return children;
  }

  return <RevealGuardInner>{children}</RevealGuardInner>;
}

function RevealGuardInner({ children }) {
  const { user, token } = useAuth();
  const [checking, setChecking] = useState(true);
  const [shouldReveal, setShouldReveal] = useState(false);

  useEffect(() => {
    if (!user || user.has_seen_reveal) {
      setChecking(false);
      return;
    }

    // Kill-switch gate: until the backend flips
    // ``reveal_enabled_for_all`` to true, only ``is_test_user`` accounts
    // proceed past this point. Everyone else short-circuits and renders
    // the wrapped route normally — no snapshot fetch, no reveal redirect.
    // When the flag is flipped on Render, the next mount picks it up
    // and the F&F cohort sees the reveal on their next /dashboard visit.
    featureFlagsApi
      .get()
      .catch(() => ({}))
      .then((flags) => {
        const globallyEnabled = Boolean(flags?.reveal_enabled_for_all);
        const allowedForUser = Boolean(user.is_test_user);
        if (!globallyEnabled && !allowedForUser) {
          setChecking(false);
          return;
        }

        rciApi
          .snapshot(user.id, token)
          .then((snap) => {
            if (!snap) {
              setChecking(false);
              return;
            }
            const daysInApp = snap.total_snapshots || 0;
            const isSafetyGated = snap.coherence_status === 'SAFETY_GATED';

            if (daysInApp >= 7 && !isSafetyGated) {
              // Eager warm-up: kick off insight generation NOW (fire-and-forget)
              // so it's underway/done by the time RevealScreen mounts and
              // fetches. The backend coalesces this with the screen's own fetch
              // (per-user lock) so it generates exactly once.
              insightsApi.get(user.id, token).catch(() => {});
              setShouldReveal(true);
            }
            setChecking(false);
          })
          .catch(() => {
            // On error, let the wrapped route render — we'd rather show
            // a working dashboard than block the user behind a guard that
            // can't make up its mind.
            setChecking(false);
          });
      });
  }, [user, token]);

  if (checking) {
    // Brief blank screen matching the reveal's own loading aesthetic.
    // Without this we'd render the dashboard first and then snap to
    // the reveal — a jarring flicker for the user's first reveal moment.
    return (
      <div
        style={{
          minHeight: '100vh',
          background: '#F2EDE3',
        }}
      />
    );
  }

  if (shouldReveal) {
    return <Navigate to="/reveal" replace />;
  }

  return children;
}
